/**
 * PushEnrollmentModal — strong-nudge modal that auto-opens once every
 * 7 days for authenticated users whose browser notification permission
 * is still `default` (i.e. never granted, never explicitly denied).
 *
 * Why a modal? The inline PWA card is easy to skip past on mobile —
 * users have been reporting they miss critical letter/leave updates
 * because they never enabled push. A blocking modal is the strongest
 * nudge browsers allow without violating the "user gesture required
 * for Notification.requestPermission()" security policy.
 *
 * Behavior:
 *   • Skips completely if permission is already granted, denied, or
 *     the browser doesn't support notifications.
 *   • Opens automatically ~2s after mount (giving the dashboard time
 *     to render first) — but only if the last dismissal was > 7 days
 *     ago (or never).
 *   • Uses `localStorage` so the cooldown is per-device, per-browser.
 */
import React, { useEffect, useState } from 'react';
import { Bell, ShieldCheck, EnvelopeSimpleOpen, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { ensurePushEnabled, permissionState } from '@/utils/pwa';
import { toast } from 'sonner';

const DISMISS_KEY = 'lucent_push_prompt_dismissed_at';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function shouldShow() {
  const perm = permissionState();
  if (perm !== 'default') return false;      // granted/denied/unsupported → skip
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return true;
    const last = parseInt(raw, 10);
    if (Number.isNaN(last)) return true;
    return Date.now() - last > COOLDOWN_MS;
  } catch { return true; }
}

export default function PushEnrollmentModal() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [open, setOpen] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  useEffect(() => {
    // Small delay so the dashboard paint finishes first.
    const t = setTimeout(() => {
      if (shouldShow()) setOpen(true);
    }, 1500);
    return () => clearTimeout(t);
  }, []);

  const remindLater = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setOpen(false);
  };

  const enable = async () => {
    setEnrolling(true);
    try {
      const res = await ensurePushEnabled();
      if (res.granted) {
        toast.success(isAr ? 'تم تفعيل الإشعارات ✓' : 'Notifications enabled ✓');
        // Clear the cooldown so if the user ever revokes we can re-prompt.
        try { localStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
        setOpen(false);
      } else if (res.reason === 'denied') {
        toast.error(isAr
          ? 'تم رفض الإشعارات — يمكنك تفعيلها لاحقاً من إعدادات المتصفح.'
          : 'Notifications blocked — enable later from your browser settings.');
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
        setOpen(false);
      } else {
        toast.info(isAr ? 'لم يتم التفعيل — سنذكرك لاحقاً.' : 'Not enabled — we\'ll remind you later.');
        remindLater();
      }
    } finally {
      setEnrolling(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) remindLater(); }}>
      <DialogContent
        dir={isAr ? 'rtl' : 'ltr'}
        className="sm:max-w-[460px] p-0 overflow-hidden"
        data-testid="push-enrollment-modal"
      >
        {/* Hero */}
        <div className="bg-gradient-to-br from-[#655dc6] to-[#4a44a3] text-white px-6 pt-6 pb-5 relative">
          <button
            onClick={remindLater}
            className="absolute top-3 end-3 text-white/70 hover:text-white transition-colors p-1 rounded-full hover:bg-white/10"
            aria-label={isAr ? 'إغلاق' : 'Close'}
            data-testid="push-modal-close"
          >
            <X size={18} weight="bold" />
          </button>
          <div className="w-14 h-14 rounded-2xl bg-white/15 backdrop-blur-sm flex items-center justify-center mb-3">
            <Bell size={30} weight="duotone" className="text-white" />
          </div>
          <DialogHeader className="text-start p-0 space-y-1">
            <DialogTitle className="text-white text-xl font-bold leading-tight">
              {isAr ? 'فعّل الإشعارات لتلقّي تحديثات لوسنت الفورية' : 'Turn on notifications for instant Lucent updates'}
            </DialogTitle>
            <DialogDescription className="text-white/80 text-[13px] leading-relaxed">
              {isAr
                ? 'لا تفوّت اعتماد إجازة، صرف راتب، أو جاهزية خطاب رسمي.'
                : 'Never miss a leave approval, payslip, or letter update.'}
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Benefits */}
        <div className="px-6 py-5 space-y-3">
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-9 h-9 rounded-lg bg-emerald-50 border border-emerald-200 flex items-center justify-center text-emerald-600">
              <EnvelopeSimpleOpen size={18} weight="duotone" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#2D2A5C]">
                {isAr ? 'وصول فوري' : 'Instant delivery'}
              </p>
              <p className="text-xs text-[#655dc6]/70 leading-relaxed">
                {isAr
                  ? 'أسرع من البريد الإلكتروني — حتى لو كان التطبيق مغلقاً.'
                  : 'Faster than email — even when the app is closed.'}
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="shrink-0 w-9 h-9 rounded-lg bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-600">
              <ShieldCheck size={18} weight="duotone" />
            </span>
            <div>
              <p className="text-sm font-semibold text-[#2D2A5C]">
                {isAr ? 'خصوصية تامة' : 'Fully private'}
              </p>
              <p className="text-xs text-[#655dc6]/70 leading-relaxed">
                {isAr
                  ? 'الإشعارات مشفّرة وتُرسل فقط لجهازك — ولن نستخدمها للإعلانات.'
                  : 'Encrypted, sent only to your device — never used for ads.'}
              </p>
            </div>
          </div>

          <p className="text-[11px] text-[#655dc6]/60 leading-relaxed pt-2 border-t border-[#C4BFF0]/50">
            {isAr
              ? 'يمكنك إيقاف الإشعارات في أي وقت من إعدادات المتصفح أو الجهاز.'
              : 'You can turn notifications off any time from your browser or device settings.'}
          </p>
        </div>

        {/* Actions */}
        <div className="px-6 pb-5 pt-1 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={remindLater}
            className="text-[#655dc6] hover:bg-[#EDE9FE]"
            data-testid="push-modal-later-btn"
          >
            {isAr ? 'لاحقاً' : 'Maybe later'}
          </Button>
          <Button
            onClick={enable}
            disabled={enrolling}
            className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
            data-testid="push-modal-enable-btn"
          >
            <Bell size={16} weight="duotone" />
            {enrolling
              ? (isAr ? 'جارٍ التفعيل…' : 'Enabling…')
              : (isAr ? 'تفعيل الإشعارات الآن' : 'Enable notifications')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
